import React from 'react';

const parseDate = (str) => {
  if (!str) return null;
  return new Date(str + 'T12:00:00');
};

const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  return Math.round((b - a) / 86400000);
};

const fmtFull = (dateStr) => {
  if (!dateStr) return '';
  const d = parseDate(dateStr);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const todayStr = () => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

// ── KPI card ────────────────────────────────────────────────────────────────────

const KPICard = ({ value, label, icon, variant }) => (
  <div className="rp-kpi-card">
    <div className={`rp-kpi-icon ${variant}`}>{icon}</div>
    <div>
      <div className="rp-kpi-value">{value}</div>
      <div className="rp-kpi-label">{label}</div>
    </div>
  </div>
);

// ── Status donut ────────────────────────────────────────────────────────────────

const StatusDonut = ({ groups }) => {
  const rows = groups.flatMap(g => g.rows || []);
  const total = rows.length || 1;
  const counts = { complete: 0, onTrack: 0, atRisk: 0, other: 0 };
  rows.forEach(r => {
    const s = r.status || '';
    if (s === 'complete' || s === 'completed') counts.complete++;
    else if (s === 'on-track' || s === 'in-progress') counts.onTrack++;
    else if (s === 'at-risk' || s === 'behind' || s === 'blocked') counts.atRisk++;
    else counts.other++;
  });
  const pComplete = Math.round((counts.complete / total) * 100);
  const pOnTrack = Math.round((counts.onTrack / total) * 100);
  const pAtRisk = Math.round((counts.atRisk / total) * 100);
  const pOther = 100 - pComplete - pOnTrack - pAtRisk;

  const d1 = (counts.complete / total) * 360;
  const d2 = (counts.onTrack / total) * 360;
  const d3 = (counts.atRisk / total) * 360;
  const gradient = `conic-gradient(
    var(--color-success) 0deg ${d1}deg,
    var(--color-brand) ${d1}deg ${d1 + d2}deg,
    var(--color-warning) ${d1 + d2}deg ${d1 + d2 + d3}deg,
    var(--color-bg-active) ${d1 + d2 + d3}deg 360deg
  )`;

  const legend = [
    { color: 'var(--color-success)', label: 'Complete', pct: pComplete, count: counts.complete },
    { color: 'var(--color-brand)', label: 'On Track', pct: pOnTrack, count: counts.onTrack },
    { color: 'var(--color-warning)', label: 'At Risk', pct: pAtRisk, count: counts.atRisk },
    { color: 'var(--color-bg-active)', label: 'Not Started', pct: pOther, count: counts.other },
  ];

  return (
    <div className="rp-dash-card">
      <h3>Task Distribution</h3>
      <div className="rp-donut" style={{ background: gradient }}>
        <div className="rp-donut-hole">
          <span className="rp-donut-pct">{pComplete}%</span>
          <span className="rp-donut-sub">Done</span>
        </div>
      </div>
      <div className="rp-legend">
        {legend.map(l => (
          <div key={l.label} className="rp-legend-item">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="rp-legend-dot" style={{ background: l.color }} />
              <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)' }}>{l.label}</span>
            </div>
            <span style={{ color: 'var(--color-text-primary)', fontWeight: 600, fontSize: 'var(--text-xs)' }}>
              {l.count}{' '}
              <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>({l.pct}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Condensed timeline ──────────────────────────────────────────────────────────

const CondensedTimeline = ({ groups }) => {
  const allDates = groups.flatMap(g =>
    (g.rows || []).flatMap(r => [r.start_date, r.end_date]).filter(Boolean)
  );
  if (!allDates.length) return null;

  const sorted = [...allDates].sort();
  const minDate = parseDate(sorted[0]);
  const maxDate = parseDate(sorted[sorted.length - 1]);
  const totalDays = Math.max(daysBetween(minDate, maxDate), 1);

  return (
    <div className="rp-dash-card" style={{ gridColumn: '1 / -1' }}>
      <h3>Project Timeline</h3>
      <div className="rp-timeline-chart">
        {groups.map(g => {
          const rowDates = (g.rows || []).filter(r => r.start_date && r.end_date && !r.is_milestone);
          if (!rowDates.length) return null;

          const gMin = rowDates.reduce((m, r) => {
            const d = parseDate(r.start_date);
            return !m || d < m ? d : m;
          }, null);
          const gMax = rowDates.reduce((m, r) => {
            const d = parseDate(r.end_date);
            return !m || d > m ? d : m;
          }, null);
          if (!gMin || !gMax) return null;

          const leftPct = (daysBetween(minDate, gMin) / totalDays) * 100;
          const widthPct = Math.max((daysBetween(gMin, gMax) / totalDays) * 100, 1);
          const avgPct = Math.round(rowDates.reduce((s, r) => s + (r.percent_complete || 0), 0) / rowDates.length);

          return (
            <div key={g.id} className="rp-timeline-row">
              <span className="rp-timeline-label" style={{ color: g.color, fontWeight: 600 }}>{g.name}</span>
              <div className="rp-timeline-track">
                <div className="rp-timeline-bg" style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: g.color }} />
                <div className="rp-timeline-fill" style={{ left: `${leftPct}%`, width: `${widthPct * avgPct / 100}%`, background: g.color }} />
              </div>
              <span className="rp-timeline-pct">{avgPct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Upcoming milestones ─────────────────────────────────────────────────────────

const UpcomingMilestones = ({ groups }) => {
  const todayD = parseDate(todayStr());
  const milestones = groups
    .flatMap(g => (g.rows || []).filter(r => r.is_milestone).map(r => ({ ...r, groupColor: g.color })))
    .filter(r => {
      const d = parseDate(r.start_date || r.end_date);
      return d && d >= todayD;
    })
    .sort((a, b) => parseDate(a.start_date || a.end_date) - parseDate(b.start_date || b.end_date))
    .slice(0, 5);

  return (
    <div className="rp-dash-card" style={{ gridColumn: '1 / -1' }}>
      <h3>Upcoming Milestones</h3>
      {milestones.length === 0 ? (
        <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
          No upcoming milestones
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {milestones.map(m => {
            const dateStr = m.start_date || m.end_date;
            const d = parseDate(dateStr);
            const daysUntil = daysBetween(todayD, d);
            const urgency = daysUntil <= 7 ? 'var(--color-danger)'
              : daysUntil <= 30 ? 'var(--color-warning)'
              : 'var(--color-success)';
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--color-bg-sunken)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-light)',
              }}>
                <div style={{ width: 10, height: 10, background: urgency, borderRadius: 2, transform: 'rotate(45deg)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 500 }}>{m.name}</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{fmtFull(dateStr)}</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: urgency,
                  background: `color-mix(in srgb, ${urgency} 15%, transparent)`,
                  padding: '2px 6px', borderRadius: 4,
                }}>
                  {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Executive Dashboard ─────────────────────────────────────────────────────────

const ExecutiveDashboard = ({ groups }) => {
  const rows = groups.flatMap(g => g.rows || []);
  const total = rows.length;
  const onTrack = rows.filter(r => ['on-track', 'in-progress', 'complete', 'completed'].includes(r.status)).length;
  const atRisk = rows.filter(r => ['at-risk', 'behind', 'blocked'].includes(r.status)).length;
  const totalBudget = rows.reduce((s, r) => s + (parseFloat(r.budget) || 0), 0);

  return (
    <div className="rp-dashboard">
      {/* KPI cards */}
      <div className="rp-kpi-grid">
        <KPICard value={total} label="Total Tasks" variant="brand"
          icon={<svg viewBox="0 0 20 20" fill="currentColor"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/></svg>}
        />
        <KPICard value={onTrack} label="On Track" variant="success"
          icon={<svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>}
        />
        <KPICard value={atRisk} label="At Risk" variant="warning"
          icon={<svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>}
        />
        <KPICard
          value={totalBudget > 0 ? `$${(totalBudget / 1000).toFixed(0)}k` : '—'}
          label="Budget Tracked"
          variant="danger"
          icon={<svg viewBox="0 0 20 20" fill="currentColor"><path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/></svg>}
        />
      </div>

      {/* Charts row */}
      <div className="rp-dash-grid">
        <StatusDonut groups={groups} />
        <div className="rp-dash-card">
          <h3>Overall Progress</h3>
          {groups.map(g => {
            const taskRows = (g.rows || []).filter(r => !r.is_milestone);
            if (!taskRows.length) return null;
            const avg = Math.round(taskRows.reduce((s, r) => s + (r.percent_complete || 0), 0) / taskRows.length);
            return (
              <div key={g.id} style={{ marginBottom: 'var(--space-3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>{g.name}</span>
                  <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-primary)' }}>{avg}%</span>
                </div>
                <div style={{ height: 8, background: 'var(--color-bg-active)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${avg}%`, background: g.color, borderRadius: 4, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
          {groups.every(g => !(g.rows || []).filter(r => !r.is_milestone).length) && (
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>No tasks yet</p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="rp-dash-grid" style={{ gridTemplateColumns: '1fr' }}>
        <CondensedTimeline groups={groups} />
      </div>

      {/* Upcoming milestones */}
      <div className="rp-dash-grid" style={{ gridTemplateColumns: '1fr' }}>
        <UpcomingMilestones groups={groups} />
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
